import { create } from 'zustand';
import { getCurrentUser } from 'aws-amplify/auth';
import apiClient from './api/apiClient';
import { FILTER_CACHE_TTL, SEARCH_OPTIONS_LIMIT } from '../shared/config/constants';

const isFetchOptions = (value) =>
  value != null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.keys(value).every((key) => key === 'force');

const useUserStore = create((set, get) => ({
  user: null,
  users: [],
  totalUsers: 0,
  totalPages: 0,
  authReady: false,
  _listCache: null,

  setUser: (user) => set({ user }),
  clearUser: () => set({ user: null }),

  bootstrapSession: async () => {
    try {
      const localId = localStorage.getItem('user_id');
      if (!localId) {
        set({ user: null, authReady: true });
        return null;
      }
      const user = await getCurrentUser();
      if (user?.username === localId) {
        set({ user, authReady: true });
        return user;
      }
      localStorage.removeItem('user_id');
      set({ user: null, authReady: true });
      return null;
    } catch {
      set({ user: null, authReady: true });
      return null;
    }
  },

  searchUsers: async ({ search = '', orgId = '', limit = SEARCH_OPTIONS_LIMIT, filterAdmins = false } = {}) => {
    try {
      const data = await apiClient.get('/admin/user/get-all', {
        search,
        orgId: orgId || '',
        limit,
        filterAdmins: !!filterAdmins,
      });
      return data.users || [];
    } catch (error) {
      console.error('Failed to search users:', error);
      return [];
    }
  },

  fetchUsers: async (page, search = '', orgId = '', filterAdmins = false, options) => {
    try {
      let force = false;
      let resolvedPage = page;
      let resolvedSearch = search;
      let resolvedOrgId = orgId;
      let resolvedFilterAdmins = filterAdmins;

      if (isFetchOptions(page)) {
        force = !!page.force;
        resolvedPage = undefined;
        resolvedSearch = '';
        resolvedOrgId = '';
        resolvedFilterAdmins = false;
      } else if (isFetchOptions(options)) {
        force = !!options.force;
      } else if (isFetchOptions(filterAdmins)) {
        force = !!filterAdmins.force;
        resolvedFilterAdmins = false;
      }

      const params = {
        ...(resolvedPage != null && resolvedPage !== '' ? { page: resolvedPage } : {}),
        search: resolvedSearch ?? '',
        orgId: resolvedOrgId ?? '',
        filterAdmins: !!resolvedFilterAdmins,
      };
      const cacheKey = JSON.stringify(params);
      const entry = get()._listCache;
      const ttl = FILTER_CACHE_TTL.users;
      const fresh =
        entry &&
        entry.key === cacheKey &&
        (ttl === Infinity || Date.now() - entry.fetchedAt < ttl);

      if (!force && fresh) {
        return get().users;
      }

      const data = await apiClient.get('/admin/user/get-all', params);

      set({
        users: data.users,
        totalUsers: data.totalUsers,
        totalPages: data.totalPages,
        _listCache: { key: cacheKey, fetchedAt: Date.now() },
      });
      return data.users;
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  },

  fetchUserById: async (id) => {
    try {
      const data = await apiClient.get(`/admin/user/${id}`);
      return data.user;
    } catch (error) {
      console.error('Failed to fetch user by ID:', error);
      throw error;
    }
  },

  createUser: async (userData) => {
    try {
      const data = await apiClient.post('/admin/user/create', userData);
      set((state) => ({
        users: [...state.users, data.user],
        _listCache: null,
      }));
      return data.user;
    } catch (error) {
      console.error('Failed to create user:', error);
      throw error;
    }
  },

  editUser: async (id, userData) => {
    try {
      const data = await apiClient.patch(`/admin/user/edit/${id}`, userData);
      set((state) => ({
        users: state.users.map((user) => (user.id === id ? data.user : user)),
        _listCache: null,
      }));
      return data.user;
    } catch (error) {
      console.error('Failed to edit user:', error);
      throw error;
    }
  },

  deleteUser: async (id) => {
    try {
      const data = await apiClient.delete(`/admin/user/delete/${id}`);
      set((state) => ({
        users: state.users.filter((user) => user.id !== id),
        _listCache: null,
      }));
      return data.message;
    } catch (error) {
      console.error('Failed to delete user:', error);
      throw error;
    }
  },

  setUserPassword: async (userId, newPassword) => {
    try {
      const data = await apiClient.post('/admin/user/admin-set-password', {
        userId,
        newPassword,
      });
      return data.message;
    } catch (error) {
      throw error;
    }
  },

  sendOTP: async (email) => {
    try {
      const data = await apiClient.post('/admin/user/forgot-password', { email });
      return data.message;
    } catch (error) {
      throw error;
    }
  },

  resetPassword: async (email, confirmationCode, newPassword) => {
    try {
      const data = await apiClient.post('/admin/user/confirm-password', {
        email,
        confirmationCode,
        newPassword,
      });
      return data.message;
    } catch (error) {
      throw error;
    }
  },
}));

export default useUserStore;
